import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Home, Film, Scissors, Star, Shield, User, LogOut, Menu, X, MessageSquare, Database } from "lucide-react";
import YSVLogo from "@/components/YSVLogo";
import NotificationBell from "@/components/NotificationBell";

const allNavItems = [
  { path: "/home", label: "Home", icon: Home, roles: null },
  { path: "/games", label: "Games", icon: Film, roles: null },
  { path: "/clips", label: "Clips", icon: Scissors, roles: null },
  { path: "/highlights", label: "Highlights", icon: Star, roles: null },
  { path: "/messages", label: "Messages", icon: MessageSquare, roles: null },
  { path: "/admin", label: "Admin", icon: Shield, roles: ["ysv_admin", "league_admin", "pro_league_admin", "org_admin", "coach"] },
  { path: "/mainframe", label: "Mainframe", icon: Database, roles: ["ysv_admin"] },
  { path: "/profile", label: "Profile", icon: User, roles: null },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // External open request (from the mobile BottomTabBar "More" button)
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("ysv:open-nav-drawer", handler);
    return () => window.removeEventListener("ysv:open-nav-drawer", handler);
  }, []);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open]);

  const visibleItems = allNavItems.filter(item => !item.roles || item.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
    setOpen(false);
    // Hard reset to clear any cached protected-route state (ProtectedRoute was
    // racing the stale user on soft-navigate, leaving the user on /home).
    window.location.href = "/login";
  };

  return (
    <>
      {/* Top bar — logo on left, hamburger on right */}
      <div data-testid="topbar" style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        height: 'calc(60px + env(safe-area-inset-top, 0px))',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingRight: 'calc(20px + env(safe-area-inset-right, 0px))',
        paddingLeft: 'calc(20px + env(safe-area-inset-left, 0px))',
        background: '#0d0d10', borderBottom: '1px solid var(--dark-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        zIndex: 60,
      }}>
        <NavLink to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <YSVLogo size="sm" showText={true} />
        </NavLink>

        <button
          data-testid="menu-toggle"
          onClick={() => setOpen(o => !o)}
          aria-label={open ? "Close menu" : "Open menu"}
          style={{
            width: 48, height: 48, borderRadius: 12,
            background: open ? 'rgba(0,229,255,0.12)' : 'rgba(255,255,255,0.05)',
            border: '1px solid var(--dark-border)',
            color: open ? 'var(--cyan)' : '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s ease',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Backdrop */}
      {open && (
        <div
          data-testid="nav-backdrop"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 70, backdropFilter: 'blur(4px)',
            animation: 'navFade 0.18s ease',
          }}
        />
      )}

      {/* Drawer (slides from right) */}
      <aside
        data-testid="nav-drawer"
        style={{
          position: 'fixed', top: 0, right: 0, height: '100vh',
          width: 'min(88vw, 320px)', background: '#0d0d10',
          borderLeft: '1px solid var(--dark-border)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.22s cubic-bezier(.2,.8,.2,1)',
          boxShadow: open ? '-24px 0 48px rgba(0,0,0,0.6)' : 'none',
          zIndex: 80,
          display: 'flex', flexDirection: 'column',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {/* Drawer header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px', borderBottom: '1px solid var(--dark-border)',
        }}>
          <YSVLogo size="sm" />
          <button
            data-testid="nav-close"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'rgba(255,255,255,0.04)', border: '1px solid var(--dark-border)',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          ><X size={18} /></button>
        </div>

        {/* Nav links */}
        <nav style={{ flex: 1, padding: '12px', overflowY: 'auto' }}>
          <div style={{ padding: '0 4px 6px' }}>
            <NotificationBell collapsed={false} />
          </div>

          {visibleItems.map(item => {
            const active = location.pathname.startsWith(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '12px 14px', borderRadius: 10,
                  textDecoration: 'none', marginBottom: 4,
                  color: active ? 'var(--cyan)' : '#e6e6ea',
                  background: active ? 'rgba(0,229,255,0.08)' : 'transparent',
                  fontSize: 15, fontWeight: active ? 700 : 500,
                  transition: 'all 0.12s ease',
                }}
              >
                <item.icon size={18} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* User footer */}
        {user && (
          <div style={{
            padding: 16, borderTop: '1px solid var(--dark-border)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: '#ff6b00', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, color: '#fff',
            }}>{user.name?.charAt(0) || 'U'}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</p>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{user.role?.replace('_', ' ')}</p>
            </div>
            <button
              data-testid="nav-logout"
              onClick={handleLogout}
              aria-label="Log out"
              title="Log out"
              style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'rgba(244,67,54,0.12)', border: '1px solid rgba(244,67,54,0.3)',
                color: '#f44336', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            ><LogOut size={16} /></button>
          </div>
        )}
      </aside>

      <style>{`
        @keyframes navFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </>
  );
};

export default Sidebar;
